import React from 'react';
import { formatUTCDate } from '../../utils/utils';
import UserDetailLink from '../common/UserDetailLink';

/**
 * Firebase Feedback list table (feature 067-user-feedback-tabs).
 *
 * Uses the shared `au-` table system so this tab is indistinguishable from its three neighbours.
 *
 * The Message column shows the first 200 characters of the member's words, wrapped rather than
 * clipped to one line, and anything longer opens IN FULL in a popup. A truncated complaint is a
 * complaint nobody can act on, and this tab has no detail page to open instead — so the cut text
 * always comes with the control that reveals the rest. The column is width-capped in CSS so a
 * long message wraps inside the table instead of shoving the other five columns off-screen.
 *
 * TWO THINGS THIS COMPONENT EXISTS TO GET RIGHT:
 *
 * 1. THIS COLLECTION SPANS FOUR APP GENERATIONS. A missing name, email, title or date is the
 *    norm here, not a fault — so every gap gets an explicit placeholder. A blank cell on a third
 *    of the rows would read as a rendering bug and send someone hunting for one.
 *
 * 2. AN UNRATED RECORD IS NOT A ZERO-RATED ONE. 45 of 154 documents predate the app asking for a
 *    rating at all. They render "—". A genuine 0 renders 0. Collapsing the two would invent a
 *    damning score for a third of the collection.
 *
 * Text is rendered as TEXT. React escapes by default and that default IS the security control:
 * titles and descriptions are untrusted member input, so dangerouslySetInnerHTML must never
 * appear in this file (FR-033).
 */

const MISSING = <span className="ff-unavailable">—</span>;

/** How much of a message the column itself shows. The rest lives in the popup. */
export const MESSAGE_LIMIT = 200;

function ratingCell(rating) {
  if (rating === null || rating === undefined) return MISSING;
  const tone = rating >= 4 ? 'high' : rating >= 3 ? 'mid' : 'low';
  return <span className={`ff-rating ff-rating--${tone}`}>{rating}</span>;
}

export default function FirebaseFeedbackTable({ items, onOpenMessage }) {
  return (
    <div className="au-table-wrap">
      <table className="au-table ff-table">
        <thead>
          <tr>
            <th>From</th>
            <th>Email</th>
            <th>Title</th>
            <th className="ff-message-head">Message</th>
            <th>Rating</th>
            <th>Submitted</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((it) => (
            <tr key={it.id}>
              <td><UserDetailLink userId={it.user_id} fallback={<span className="ff-unavailable">Not provided</span>}>{it.user_name}</UserDetailLink></td>
              <td className="ff-email-cell"><UserDetailLink userId={it.user_id} fallback={MISSING} testId="user-detail-email-link">{it.user_email}</UserDetailLink></td>
              <td className="ff-title-cell">{it.title || MISSING}</td>
              <td className="ff-message-cell">
                {it.message ? (
                  <>
                    <span className="ff-message">
                      {it.message.length > MESSAGE_LIMIT
                        ? `${it.message.slice(0, MESSAGE_LIMIT)}…`
                        : it.message}
                    </span>
                    {it.message.length > MESSAGE_LIMIT && (
                      <button
                        type="button"
                        className="au-link ff-read-more"
                        onClick={() => onOpenMessage && onOpenMessage(it)}
                      >
                        Read full message
                      </button>
                    )}
                  </>
                ) : (
                  <span className="ff-unavailable">No message</span>
                )}
              </td>
              <td>{ratingCell(it.rating)}</td>
              <td className="ff-submitted">
                {formatUTCDate(it.created_display) || <span className="ff-unavailable">Unknown</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
